"use client";

import { useState, useEffect, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import getApp from "@/lib/firebase";

interface LinkMetadata {
  title: string | null;
  siteName: string | null;
  imageUrl: string | null;
  description: string | null;
  url: string;
}

// Client-side cache shared across all hook instances
const metadataCache = new Map<string, LinkMetadata>();

export function useLinkMetadata(url: string | null): {
  metadata: LinkMetadata | null;
  loading: boolean;
} {
  const [metadata, setMetadata] = useState<LinkMetadata | null>(
    url ? metadataCache.get(url) ?? null : null
  );
  const [loading, setLoading] = useState(false);
  const urlRef = useRef(url);
  urlRef.current = url;

  useEffect(() => {
    if (!url) {
      setMetadata(null);
      setLoading(false);
      return;
    }

    // Check cache
    const cached = metadataCache.get(url);
    if (cached) {
      setMetadata(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchMetadata = async () => {
      try {
        const functions = getFunctions(getApp());
        const fetchLink = httpsCallable<{ url: string }, LinkMetadata>(
          functions,
          "fetchLinkMetadata"
        );
        const result = await fetchLink({ url });

        if (!cancelled && urlRef.current === url) {
          metadataCache.set(url, result.data);
          setMetadata(result.data);
        }
      } catch (err) {
        console.error("Failed to fetch link metadata:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMetadata();
    return () => { cancelled = true; };
  }, [url]);

  return { metadata, loading };
}
